import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { IApiClient } from '../data/client';
import { HttpError } from '../data/HttpError';
import {
  GetConfigurationByCodeQuery,
  GetProductStructureByIdQuery,
  ProductConfigurationOrigin,
  ShoppingContext,
} from '../data/model';
import { BasePageState } from '../shared/BasePageState';
import { IMemento } from '../shared/common';
import { ILoadingIndicator, LoadingIndicator } from '../shared/LoadingIndicator';
import { StatusCodes } from '../shared/StatusCodes';
import { StoreState } from '../StoreState';
import { ConfigurationCodeState } from './components/ConfigurationCode/ConfigurationCodeState';
import { ConfigurationRestorerState } from './components/ConfigurationRestorer/ConfigurationRestorerState';
import { RawConfiguratorState } from './components/RawConfigurator/RawConfiguratorState';
import EmbeddableRawConfiguratorPageQueryParser from './EmbeddableRawConfiguratorPageQueryParser';

export interface IEmbeddableRawConfiguratorPage extends IMemento {
  shoppingContext: ShoppingContext;
}

export class EmbeddableRawConfiguratorPageState extends BasePageState<IMemento> {
  public dataLoadingIndicator: ILoadingIndicator;
  public configuratorState: RawConfiguratorState;
  public configurationCodeState: ConfigurationCodeState;
  public configurationRestorerState: ConfigurationRestorerState;

  @observable
  public errorMessage: string = '';

  public isEmbedded: boolean;
  public code?: string;

  @computed
  public get isLoading(): boolean {
    return this.loadingIndicator.isLoading || this.dataLoadingIndicator.isLoading;
  }

  private shoppingContext: ShoppingContext;

  public constructor(
    private readonly client: IApiClient,
    private readonly loadingIndicator: ILoadingIndicator,
    private readonly productId: string,
    query: Map<string, string>,
  ) {
    super({ pageTitleFormat: 'Raw configurator', pageDescriptionFormat: '' });

    makeObservable(this);

    const { embed, size, code } = this.parseQuery(query);

    this.isEmbedded = embed;
    this.code = code;
    this.dataLoadingIndicator = new LoadingIndicator();
    this.configuratorState = new RawConfiguratorState(productId, size, this.client);
    this.configurationCodeState = new ConfigurationCodeState(this.configuratorState, client);
    this.configurationRestorerState = new ConfigurationRestorerState();

    this.notifyParentWhenEmbeddedInIFrame();
  }

  private notifyParentWhenEmbeddedInIFrame() {
    reaction(
      () => this.configurationCodeState.configurationCode,
      (configurationCode) => {
        if (window?.parent) {
          window.parent.postMessage({ type: 'configurationCode', configurationCode }, '*');
        }
      },
    );
  }

  public async onLoad(store: StoreState) {
    this.initialize(store.shoppingContext);
  }

  private initialize(shoppingContext: ShoppingContext) {
    this.shoppingContext = shoppingContext;
  }

  @action
  public async onLoadAdditionalData() {
    try {
      this.dataLoadingIndicator.start();

      const structure = await this.loadStructure();
      const configuration = await this.loadConfiguration();

      this.configuratorState.initialize(this.shoppingContext, structure);

      if (
        configuration &&
        configuration.origin === ProductConfigurationOrigin.RawConfigurator &&
        configuration.baseModelCode === structure.productNumber
      ) {
        await this.configurationRestorerState.restore(this.configuratorState, this.code, configuration);
      }
    } catch (error) {
      this.errorMessage = (error as Error).message;
    } finally {
      this.dataLoadingIndicator.stop();
    }
  }

  private async loadStructure() {
    try {
      const structureResponse = await this.client.send(
        new GetProductStructureByIdQuery({
          productId: this.productId,
          ignoreIrrelevantFeaturesAndOptions: true,
          shoppingContext: this.shoppingContext,
        }),
      );

      return structureResponse.structure;
    } catch (error) {
      const errorMessage =
        (error as HttpError).status === StatusCodes.NotFound
          ? `Product structure not found for product ${this.productId}. Make sure it's properly configured in the Product Structure Extractor.`
          : `Unable to load product structure for product ${this.productId}`;

      throw new Error(errorMessage);
    }
  }

  private async loadConfiguration() {
    if (!this.code) {
      return null;
    }

    try {
      const codeResponse = await this.client.send(
        new GetConfigurationByCodeQuery({
          code: this.code,
        }),
      );

      return codeResponse.productConfiguration;
    } catch (error) {
      const errorMessage =
        (error as HttpError).status === StatusCodes.NotFound
          ? `Configuration code not found ${this.code}.`
          : `Unable to load configuration code ${this.code}`;

      throw new Error(errorMessage);
    }
  }

  getMemento(): IEmbeddableRawConfiguratorPage {
    return {
      shoppingContext: this.shoppingContext,
    };
  }

  restoreMemento(memento: IEmbeddableRawConfiguratorPage) {
    this.initialize(memento.shoppingContext);
  }

  private parseQuery(query: Map<string, string>) {
    const { embed, size, code } = EmbeddableRawConfiguratorPageQueryParser.toModel(query);
    return { embed, size: size ?? 'medium', code };
  }
}
